import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  HStack,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FaChevronLeft, FaChevronRight, FaExclamationTriangle, FaLightbulb } from 'react-icons/fa';

/**
 * Carrusel de consejos / advertencias (manual + auto) en una sola caja compacta.
 * @param {{ id: string, title: string, content: React.ReactNode, icon?: import('react-icons').IconType }[]} slides
 * @param {'info' | 'warning'} [status]
 */
export default function ProfileTipsCarousel({
  slides = [],
  autoPlayMs = 8000,
  status = 'info',
  mb = 6,
  'aria-label': ariaLabel,
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = slides.length;
  const isWarning = status === 'warning';

  useEffect(() => {
    setIndex((i) => (count > 0 ? Math.min(i, count - 1) : 0));
  }, [count]);

  const go = useCallback(
    (delta) => {
      if (count <= 0) return;
      setIndex((i) => (i + delta + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count <= 1 || paused) return undefined;
    const id = setInterval(() => go(1), autoPlayMs);
    return () => clearInterval(id);
  }, [count, autoPlayMs, go, paused]);

  if (!count) return null;

  const safeIndex = Math.min(Math.max(0, index), count - 1);
  const slide = slides[safeIndex];
  if (!slide) return null;

  const SlideIcon = slide.icon || (isWarning ? FaExclamationTriangle : FaLightbulb);
  const colorScheme = isWarning ? 'orange' : 'blue';

  return (
    <Alert.Root
      status={isWarning ? 'warning' : 'info'}
      variant='subtle'
      rounded="xl"
      mb={mb}
      bg={isWarning ? 'orange.50' : 'blue.50'}
      borderColor={isWarning ? 'orange.400' : 'blue.400'}
      pr={2}
      alignItems="flex-start"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      borderStartWidth='3px'
      borderStartColor='colorPalette.solid'>
      <Alert.Indicator color={`${colorScheme}.500`} mt={0.5} asChild><SlideIcon /></Alert.Indicator>
      <Box flex={1} minW={0}>
        <HStack justify="space-between" align="flex-start" gap={2}>
          <VStack align="start" gap={1} flex={1} minW={0}>
            <Text fontWeight="bold" fontSize="sm" color={`${colorScheme}.700`}>
              {slide.title}
            </Text>
            {typeof slide.content === 'string' ? (
              <Text fontSize="sm" color={`${colorScheme}.600`}>
                {slide.content}
              </Text>
            ) : (
              slide.content
            )}
          </VStack>
          {count > 1 ? (
            <HStack gap={0} flexShrink={0}>
              <IconButton
                aria-label="Anterior"
                size="sm"
                variant="ghost"
                colorPalette={colorScheme}
                onClick={() => go(-1)}><FaChevronLeft /></IconButton>
              <IconButton
                aria-label="Siguiente"
                size="sm"
                variant="ghost"
                colorPalette={colorScheme}
                onClick={() => go(1)}><FaChevronRight /></IconButton>
            </HStack>
          ) : null}
        </HStack>
        {count > 1 ? (
          <HStack
            gap={1.5}
            mt={2}
            justify="center"
            role="tablist"
            aria-label={ariaLabel || (isWarning ? 'Campos recomendados' : 'Consejos del perfil')}
          >
            {slides.map((s, i) => (
              <Box
                key={s.id}
                role="tab"
                aria-selected={i === safeIndex}
                aria-label={`${s.title}${i === safeIndex ? ', actual' : ''}`}
                w={i === safeIndex ? '16px' : '6px'}
                h="6px"
                rounded="full"
                bg={i === safeIndex ? `${colorScheme}.500` : `${colorScheme}.200`}
                transition="all 0.2s"
                cursor="pointer"
                onClick={() => setIndex(i)}
              />
            ))}
          </HStack>
        ) : null}
      </Box>
    </Alert.Root>
  );
}
